import {
  APIComponentAttributes,
  APIOrganizationUser,
  APIScopeData,
  APITagAttributes,
  ArdoqId,
  PersistedExternalDocument,
  ReportColumnType,
} from '@ardoq/api-types';
import { EnhancedScopeData } from '@ardoq/data-model';
import { parseLocaleDateString } from '@ardoq/date-time';
import { s16 } from '@ardoq/design-tokens';
import {
  DataTypeDefinition,
  GridApi,
  GridReadyEvent,
  IsFullWidthRowParams,
  RowClassParams,
  RowGroupOpenedEvent,
  themeQuartz,
  RowSelectionOptions,
  GetRowIdParams,
  CellSelectionOptions,
  ColumnMenuVisibleChangedEvent,
  ColumnPinnedEvent,
  FirstDataRenderedEvent,
  INoRowsOverlayParams,
} from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { FullWidthCellRenderer } from './CellRenderers/cellRenderers';
import { columnOperations } from './columns/columnOperations';
import {
  autosizeStrategy,
  defaultColumnDefinition,
} from './columns/defaultColumnDefinition';
import {
  SELECTION_COLUMN_DEFINITION,
  AG_GRID_SELECTION_COLUMN_ID,
} from './columns/selectAndDeleteColumnDefinition';
import { createComponentsOperations } from './createComponents/createComponentsOperations';
import { getColumnMainMenuItems } from './columns/mainColumnMenuItems';
import { toAgGridColumnDefs } from './columns/mapReportColumnDefinitionToAgGridFormat';
import { customCellRenderersByName } from './CellRenderers/custom/customCellRenderers';
import { fillHandleOperations } from './fillHandleOperations';
import { initializeInventoryAgGrid } from './initializeInventoryAgGrid';
import { useColumnChooserDismissal } from './useColumnChooserDismissal';
import { INVENTORY_PAGE_SIZE, MAX_BLOCKS_IN_CACHE } from './inventoryConsts';
import {
  ExtendedRowNode,
  FetchNextRowsBatchFunction,
  ColumnDefinitionArdoqMetadata,
  InventoryAccessControl,
  InventoryColumnDefinitionWithSelectionState,
  InventoryCommands,
  InventoryConfig,
  InventoryContext,
  InventoryDatasourceSelection,
  InventoryHierarchy,
  MetamodelWithTriplesWithOrigin,
} from './types';
import { getContextMenuItems } from './contextMenu';
import {
  NoRowsOverlayComponent,
  NoRowsOverlayComponentProps,
} from './NoRowsOverLaycomponent';
import { LoadingOverlayComponent } from './LoadingOverlayComponent';
import { registerModules } from './moduleRegistry';
import { AgEventHandlers } from './AgEventHandlers';
import { hierarchyInAgGridOperations } from './hierarchy/hierarchyInAgGridOperations';
import { inventoryGridAPIOperations } from './inventoryGridAPIOperations';
import { type I18nLocale } from '@ardoq/i18n';

registerModules();

/**
 * AG Grid requires a unique ID for each row.
 * It's possible for a row to be pinned and in the main table (when you create new row and then change filters or sorting).
 * We make sure that the ID is unique in such case.
 *
 * @deprecated will be removed when we move creating components to a drawer
 */
const getRowId = (params: GetRowIdParams<APIComponentAttributes>) => {
  return params.rowPinned
    ? createComponentsOperations.getTopRowId(params.data?._id ?? '')
    : (params.data?._id ?? '');
};

const isFullWidthRow = (params: IsFullWidthRowParams) => {
  return createComponentsOperations.isCreateComponentHelperTextRow(
    params.rowNode.data?._id ?? ''
  );
};

const getRowClass = (params: RowClassParams<APIComponentAttributes>) =>
  (params.node as ExtendedRowNode)?._hasError ? 'row-with-error' : undefined;

const baseRowSelection: RowSelectionOptions = {
  mode: 'multiRow',
  headerCheckbox: false,
  enableClickSelection: false,
};

const disabledRowSelection: RowSelectionOptions = {
  ...baseRowSelection,
  checkboxes: false,
};

const cellSelection: CellSelectionOptions = {
  suppressMultiRanges: true,
  enableHeaderHighlight: true,
  handle: {
    mode: 'fill',
    suppressClearOnFillReduction: true,
    direction: 'y',
    /**
     * Callback to fill values instead of simply copying values or increasing number values using linear progression.
     * TODO AM - we would need to implement this if we want to support reference columns and file columns
     */
    setFillValue: params => {
      if (fillHandleOperations.isDoubleClickFill(params)) {
        return params.currentCellValue;
      }
      const columnContext = params.column.getColDef()?.context as
        | ColumnDefinitionArdoqMetadata
        | undefined;
      if (columnContext?.inventoryColumnType === 'reference') {
        // Avoid accidentally deleting references when using fill handle
        return params.currentCellValue;
      }
      return false;
    },
  },
};

export type ComponentInventoryProps = {
  initialColumnDefinitions: InventoryColumnDefinitionWithSelectionState[];
  getNextRowsBatch: FetchNextRowsBatchFunction;
  height?: number;
  width?: string;
  datasourceSelection: InventoryDatasourceSelection;
  initialScopeData: EnhancedScopeData;
  apiScopeData: APIScopeData;
  users: APIOrganizationUser[];
  metaModel: MetamodelWithTriplesWithOrigin;
  accessControl: InventoryAccessControl;
  commands: InventoryCommands;
  config: InventoryConfig;
  noRowsOverlayComponentProps?: Omit<NoRowsOverlayComponentProps, 'i18nLocale'>;
  inventoryHierarchy: InventoryHierarchy;
  lastRecalculationByFieldsId: Record<ArdoqId, string>;
  externalDocumentsByComponentId: Record<ArdoqId, PersistedExternalDocument[]>;
  i18nLocale: I18nLocale;
  isLockingEnabled: boolean;
  tagsByWorkspaceId: Record<ArdoqId, APITagAttributes[]>;
  richTextEditorConfig: InventoryContext['richTextEditorConfig'];
  externalSubquery: InventoryContext['externalSubquery'];
  /**
   * Translations for AG Grid built-in strings (filter menus, column menus,
   * overlays, sort/pin labels). Forwarded to `gridOptions.localeText`.
   * Pattern A: the consumer maps from its i18n locale and supplies the map;
   * the package itself stays translation-free for AG Grid built-ins.
   */
  agGridLocaleText?: Record<string, string>;
  defaultVisibleColumnKeys?: InventoryContext['defaultVisibleColumnKeys'];
};

export const ComponentInventory = ({
  initialColumnDefinitions,
  getNextRowsBatch,
  height,
  width,
  commands,
  datasourceSelection,
  users,
  initialScopeData,
  apiScopeData,
  metaModel,
  accessControl,
  config,
  noRowsOverlayComponentProps,
  inventoryHierarchy,
  lastRecalculationByFieldsId,
  externalDocumentsByComponentId,
  i18nLocale,
  isLockingEnabled,
  tagsByWorkspaceId,
  richTextEditorConfig,
  agGridLocaleText,
  defaultVisibleColumnKeys,
  externalSubquery,
}: ComponentInventoryProps) => {
  /*
   * We want popups (date picker and dropdowns to stay inside the grid container
   * when scrolling horizontally. Therefore, we cannot use the default document.body.
   */
  const gridRef = useRef<HTMLDivElement>(null);

  const inventoryApi = useRef<GridApi<APIComponentAttributes> | undefined>(
    undefined
  );
  const visibleColumnRef = useRef<{ key: string; type: ReportColumnType }[]>(
    initialColumnDefinitions
      .filter(it => it.isVisible)
      .map(it => ({ key: it.key, type: it.type || ReportColumnType.CUSTOM }))
  );

  const { onColumnMenuVisibleChanged } =
    useColumnChooserDismissal(inventoryApi);

  const columnDefinitionsInAgGridFormat = useMemo(
    () =>
      toAgGridColumnDefs(
        initialColumnDefinitions.filter(
          /**
           * We're trimming down the scope, so we skip the references columns and file columns.
           * We will add support for them in later iterations.
           */
          reportColumn => !columnOperations.isFileColumn(reportColumn)
        ),
        {
          datasourceSelection,
          isHierarchyEnabled: inventoryHierarchy.isHierarchyEnabled,
          fixedFilterColumnKeys: config.fixedFilterColumnKeys,
        }
      ),
    [
      initialColumnDefinitions,
      datasourceSelection,
      inventoryHierarchy.isHierarchyEnabled,
      config.fixedFilterColumnKeys,
    ]
  );

  const hasAutoHeightColumn = useMemo(
    () =>
      columnOperations
        .toLeafColumnDefs(columnDefinitionsInAgGridFormat)
        .some(colDef => colDef.autoHeight),
    [columnDefinitionsInAgGridFormat]
  );

  const customDataTypeDefinitions: {
    dateString: DataTypeDefinition<APIComponentAttributes>;
  } = useMemo(
    () => ({
      dateString: {
        baseDataType: 'dateString',
        extendsDataType: 'dateString',
        dateParser: (value: string | undefined) => {
          if (!value) return undefined;
          return parseLocaleDateString(value, config.locale) ?? undefined;
        },
        dateFormatter: (value: Date | undefined) => value?.toISOString(),
      },
    }),
    [config.locale]
  );

  const initializeOnGridReady = useCallback(
    (params: GridReadyEvent) => {
      const initialContext: InventoryContext = {
        commands,
        users,
        /*
          Initial scopeData is from the request for columns. It contains all models and workspaces,
          whereas the subsequent requests (with component entries) contain only
          models and workspaces depending on the components.
          They include workspaces and models for the connected components - the once that are referenced from the components in the table.
         */
        scopeData: initialScopeData,
        apiScopeData,
        config,
        metaModel,
        datasourceSelection,
        accessControl,
        hierarchy: inventoryHierarchy,
        lastRecalculationByFieldsId,
        externalDocumentsByComponentId,
        i18nLocale,
        isLockingEnabled,
        tagsByWorkspaceId,
        richTextEditorConfig,
        defaultVisibleColumnKeys,
        externalSubquery,
      };

      inventoryApi.current = params.api;
      initializeInventoryAgGrid(
        params,
        initialContext,
        datasourceSelection,
        commands,
        getNextRowsBatch
      );
      // The selection column has no `pinned` of its own; it follows the name
      // column. Establish that here, then `handleColumnPinned` and
      // `handleDisplayedColumnsChanged` maintain it.
      AgEventHandlers.syncSelectionColumnPinnedWithName(params.api);
      AgEventHandlers.syncGroupHeaderHeightWithDisplayedGroups(params);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: i18nLocale, isLockingEnabled, tagsByWorkspaceId, defaultVisibleColumnKeys, and externalSubquery only seed the initial context; subsequent updates are pushed via the sibling effects. Including them here would force a full grid teardown on every emission if upstream ref stability ever regresses.
    [
      commands,
      users,
      initialScopeData,
      apiScopeData,
      metaModel,
      datasourceSelection,
      accessControl,
      config,
      getNextRowsBatch,
      inventoryHierarchy,
      lastRecalculationByFieldsId,
      externalDocumentsByComponentId,
      richTextEditorConfig,
    ]
  );

  // Keep context.i18nLocale in sync so AG Grid framework-locked callbacks
  // (cell renderers / value getters / formatters) and imperative confirm
  // dialog openers always read the latest locale without reaching for
  // i18nLocale$.state.
  useEffect(() => {
    const api = inventoryApi.current;
    const inventoryContext = api?.getGridOption('context') as InventoryContext;
    if (!api || !inventoryContext) return;

    inventoryContext.i18nLocale = i18nLocale;
    api.refreshHeader();
    api.refreshCells({ force: true });
  }, [i18nLocale]);

  useEffect(() => {
    const api = inventoryApi.current;
    const inventoryContext = api?.getGridOption('context') as InventoryContext;
    if (!api || !inventoryContext) return;

    inventoryContext.isLockingEnabled = isLockingEnabled;
    api.refreshCells({ force: true });
  }, [isLockingEnabled]);

  // Sync context.accessControl with incoming props so cells reflect latest
  // permissions. Particularly important for changes to confidential field
  // permissions as this could change how they render and whether they are
  // editable.
  useEffect(() => {
    const api = inventoryApi.current;
    if (!api) return;
    inventoryGridAPIOperations.updateAccessControlContext(api, accessControl);
    // Force refresh is necessary to make the change appear.
    api.refreshCells({ force: true });
  }, [accessControl]);

  useEffect(() => {
    const api = inventoryApi.current;
    const inventoryContext = api?.getGridOption('context') as InventoryContext;
    if (!api || !inventoryContext) return;

    inventoryContext.hierarchy = inventoryHierarchy;
    // Force the header to recreate so it picks up the new column definition:
    // to show or hide "Collapse all" button in Name header
    api.refreshHeader();
    // Only re-fire on actual mode flip. The prop is rebuilt on every upstream
    // emission with a fresh *empty* expandedNodeIds set; reassigning it here
    // would clobber the accumulated runtime state held in the existing
    // inventoryContext.hierarchy, so we resync only when hierarchy itself toggles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryHierarchy.isHierarchyEnabled]);

  useEffect(() => {
    const api = inventoryApi.current;
    const inventoryContext = api?.getGridOption('context') as InventoryContext;
    if (!api || !inventoryContext) return;

    inventoryContext.lastRecalculationByFieldsId = lastRecalculationByFieldsId;
  }, [lastRecalculationByFieldsId]);

  useEffect(() => {
    const api = inventoryApi.current;
    const inventoryContext = api?.getGridOption('context') as InventoryContext;
    if (!api || !inventoryContext) return;

    inventoryContext.externalDocumentsByComponentId =
      externalDocumentsByComponentId;
    api.refreshCells({ force: true });
  }, [externalDocumentsByComponentId]);

  useEffect(() => {
    const api = inventoryApi.current;
    const inventoryContext = api?.getGridOption('context') as InventoryContext;
    if (!api || !inventoryContext) return;

    inventoryContext.commands = commands;
  }, [commands]);

  useEffect(() => {
    const api = inventoryApi.current;
    const inventoryContext = api?.getGridOption('context') as InventoryContext;
    if (!api || !inventoryContext) return;

    inventoryContext.defaultVisibleColumnKeys = defaultVisibleColumnKeys;
  }, [defaultVisibleColumnKeys]);

  useEffect(() => {
    const api = inventoryApi.current;
    const inventoryContext = api?.getGridOption('context') as InventoryContext;
    if (!api || !inventoryContext) return;

    inventoryContext.tagsByWorkspaceId = tagsByWorkspaceId;
  }, [tagsByWorkspaceId]);

  useEffect(() => {
    const api = inventoryApi.current;
    const inventoryContext = api?.getGridOption('context') as InventoryContext;
    if (!api || !inventoryContext) return;
    if (inventoryContext.externalSubquery === externalSubquery) return;

    inventoryContext.externalSubquery = externalSubquery;
    api.refreshServerSide();
  }, [externalSubquery]);

  const handleColumnPanelVisibilityChanged = useCallback(
    (event: ColumnMenuVisibleChangedEvent) => {
      onColumnMenuVisibleChanged(event);

      AgEventHandlers.handleColumnMenuVisibilityChanged(
        event,
        visibleColumnRef.current
      );

      visibleColumnRef.current = event.api
        .getAllDisplayedColumns()
        .map(column => ({
          key: column.getColId(),
          type:
            column.getColDef().context?.reportColumnType ??
            ReportColumnType.CUSTOM,
        }));
    },
    [onColumnMenuVisibleChanged]
  );

  const autoSizeColumnsAfterFirstDataRendered = (
    params: FirstDataRenderedEvent<APIComponentAttributes, any>
  ) => {
    // Defer to allow React cell renderers to complete mounting before measuring
    setTimeout(() => {
      params.api.autoSizeAllColumns();
    });
  };

  const handleColumnPinned = useCallback(
    (event: ColumnPinnedEvent<APIComponentAttributes>) => {
      const affectedColIds = (
        event.columns ?? (event.column ? [event.column] : [])
      ).map(column => column.getColId());

      if (affectedColIds.includes('name')) {
        AgEventHandlers.syncSelectionColumnPinnedWithName(event.api);
      }
      if (
        affectedColIds.length === 1 &&
        affectedColIds[0] === AG_GRID_SELECTION_COLUMN_ID
      ) {
        return;
      }
      commands.notifyColumnPinned(event);
    },
    [commands]
  );

  const handleRowGroupOpened = useCallback(
    (event: RowGroupOpenedEvent<APIComponentAttributes>) => {
      // `event.event` is set only when the change came from a browser event
      // (chevron click). Programmatic expansions — e.g. re-expansion after a
      // server-side refresh via `expandRowNode` — leave it undefined, and we
      // don't want those to show up as user actions in tracking.
      if (!event.event) return;
      if (!event.expanded) {
        hierarchyInAgGridOperations.clearExpandAllMode(event.api);
      }
      commands.notifyRowExpandedChanged(event.expanded, event.node.level);
    },
    [commands]
  );

  const noRowsOverlayComponent = useMemo(
    () =>
      noRowsOverlayComponentProps
        ? (params: INoRowsOverlayParams<unknown, InventoryContext>) => (
            <NoRowsOverlayComponent
              {...noRowsOverlayComponentProps}
              i18nLocale={params.context.i18nLocale}
            />
          )
        : undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- AG Grid surface; see apps/ardoq-front/AGENTS.md
    [noRowsOverlayComponentProps]
  );

  return (
    <div
      ref={gridRef}
      className="ardoq-inventory"
      style={{
        position: 'relative', // to make the popupParent work correctly
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        height: height ? `${height}px` : '100%',
        width,
        gap: s16,
      }}
    >
      <AgGridReact<APIComponentAttributes>
        components={customCellRenderersByName}
        localeText={agGridLocaleText}
        onRowDragMove={AgEventHandlers.handleRowDragMove}
        onRowDragLeave={AgEventHandlers.handleRowDragLeave}
        onRowDragEnd={AgEventHandlers.handleRowDragEnd}
        onGridReady={initializeOnGridReady}
        theme={themeQuartz}
        loadingOverlayComponent={LoadingOverlayComponent}
        onGridPreDestroyed={commands.deregisterInventoryGridApi}
        debug={import.meta.env.DEBUG_INVENTORY}
        getRowId={getRowId}
        cacheBlockSize={INVENTORY_PAGE_SIZE}
        maxBlocksInCache={hasAutoHeightColumn ? undefined : MAX_BLOCKS_IN_CACHE}
        columnDefs={columnDefinitionsInAgGridFormat}
        rowModelType="serverSide"
        defaultColDef={defaultColumnDefinition}
        autoSizeStrategy={autosizeStrategy}
        onFirstDataRendered={autoSizeColumnsAfterFirstDataRendered}
        onCellKeyDown={AgEventHandlers.onCellKeyDown}
        onCellValueChanged={AgEventHandlers.updateComponentValue}
        noRowsOverlayComponent={noRowsOverlayComponent}
        getMainMenuItems={getColumnMainMenuItems}
        dataTypeDefinitions={customDataTypeDefinitions}
        getRowClass={getRowClass}
        isFullWidthRow={isFullWidthRow}
        fullWidthCellRenderer={FullWidthCellRenderer}
        onFilterChanged={AgEventHandlers.getOnFilterChanged}
        suppressMovableColumns={datasourceSelection.dataSourceType === 'report'}
        onColumnMoved={commands.notifyColumnMoved}
        onColumnVisible={commands.notifyColumnVisible}
        onDisplayedColumnsChanged={
          AgEventHandlers.handleDisplayedColumnsChanged
        }
        onColumnPinned={handleColumnPinned}
        onColumnMenuVisibleChanged={handleColumnPanelVisibilityChanged}
        treeData={inventoryHierarchy.isHierarchyEnabled}
        isServerSideGroup={data =>
          hierarchyInAgGridOperations.isServerSideGroup(
            data,
            inventoryApi.current
          )
        }
        getServerSideGroupKey={data => data._id}
        isServerSideGroupOpenByDefault={
          hierarchyInAgGridOperations.isServerSideGroupOpenByDefault
        }
        getChildCount={data =>
          hierarchyInAgGridOperations.getChildCount(data, inventoryApi.current)
        }
        treeDataParentIdField={'parent'}
        groupDisplayType="custom"
        onRowGroupOpened={handleRowGroupOpened}
        rowDragMultiRow={inventoryHierarchy.isHierarchyEnabled}
        cellSelection={cellSelection}
        rowSelection={
          config.isSelectionColumnDisabled
            ? disabledRowSelection
            : baseRowSelection
        }
        onPasteStart={AgEventHandlers.onPasteStart}
        onPasteEnd={AgEventHandlers.onPasteEnd}
        processDataFromClipboard={AgEventHandlers.processDataFromClipboard}
        processCellForClipboard={AgEventHandlers.processCellForClipboard}
        onFillEnd={AgEventHandlers.onFillEnd}
        suppressCsvExport={true}
        selectionColumnDef={SELECTION_COLUMN_DEFINITION}
        onSelectionChanged={AgEventHandlers.setSelectedComponentAndRefresh}
        getContextMenuItems={getContextMenuItems}
        tooltipShowDelay={0}
        onSortChanged={AgEventHandlers.onSortChanged}
      />
    </div>
  );
};
